/**
 * 原本使用tea的Upload组件用于文件上传
 * 后面发现使用cos上传文件时, 无法使用Upload组件的action参数, 导致每次上传时, Upload组件内部都要发起一个无效的请求.
 * 所以将本地上传功能单独拆出自定义一个组件.
 */

import React, { useRef, useState, useCallback } from 'react';
import './index.scss';

import { IFileUploaderProps } from './types';

const FileUploader: React.FC<IFileUploaderProps> = ({
  accept,
  multiple = false,
  beforeUpload,
  onUploadRequest,
  onError,
  children,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragCounterRef = useRef<number>(0);

  // 打开文件选择对话框
  const open = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  // 处理拖拽进入
  const handleDragEnter = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  // 处理拖拽离开
  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      setIsDragging(false);
    }
  }, []);

  // 处理拖拽悬停
  const handleDragOver = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 处理文件拖拽放置
  const handleDrop = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  // 处理文件选择
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFiles(files);
    }
    // 重置 input 值，允许重复选择相同文件
    e.target.value = '';
  }, []);

  // 处理文件验证和上传
  const handleFiles = useCallback((files: File[]): void => {
    const targetFiles = multiple ? files : [files[0]];
    
    targetFiles.forEach(file => {
      try {
        // 文件类型验证
        if (accept && accept.length > 0) {
          const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
          if (!accept.includes(fileExtension)) {
            const errorMessage = `文件类型不支持: ${fileExtension}`;
            console.warn(errorMessage);
            if (onError) {
              onError(errorMessage, file);
            }
            return;
          }
        }

        // 调用 beforeUpload 验证
        if (beforeUpload) {
          const isValid = beforeUpload(file);
          if (!isValid) {
            return;
          }
        }

        // 调用 onUploadRequest 回调
        if (onUploadRequest) {
          onUploadRequest(file);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '文件处理过程中发生未知错误';
        console.error('FileUploader error:', error);
        if (onError) {
          onError(errorMessage, file);
        }
      }
    });
  }, [accept, multiple, beforeUpload, onUploadRequest, onError]);

  // 如果 children 是函数，调用它并提供必要的参数
  if (typeof children === 'function') {
    return (
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={Array.isArray(accept) ? accept.join(',') : accept}
          multiple={multiple}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {children({ open, isDragging })}
      </div>
    );
  }

  // 如果 children 不是函数，直接渲染
  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={Array.isArray(accept) ? accept.join(',') : accept}
        multiple={multiple}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {children}
    </div>
  );
};

export default FileUploader;
